import dayjs from 'dayjs'
import { v4 } from 'uuid'

import {
  noCacheUrl,
  getFormattedOrdinals,
  getTitleAndSubtitle,
  secureUrl,
  stripHttpProtocol,
  platformStoreGet,
  platformStoreDelete,
  platformStoreSet,
  triggerSentryError,
  ComcastErrorType,
} from '../../../shared/helpers.js'
import {
  appendUserId,
  baseRequest,
  contentServiceGet,
  contentServicePut,
  contentServiceDelete,
  contentServicePatch,
  profileServiceGet,
  profileServiceDelete,
} from '../lib/utils.js'
import { getStationDonationAmountsHelper } from './tvaultActions.js'
import { registerUser } from './userActions.js'
import configuration from '../configuration.js'
import { store } from '../store.js'
import Platform from '../platforms'

const deviceKey = `deviceId-ga`
const uidKey = `uID-ga` //TODO: if localstorage is the source of truth for USER ID, then we should use that and remove user.pid

export function getStationsByAuto() {
  const url = configuration.apiUrls.stationsByAuto
  return getLocalization(url)
}

export function getAssociatedStations() {
  return getLocalization(configuration.apiUrls.associatedStations)
}

export function getStationsByIpDefault() {
  const url = configuration.apiUrls.stationsByIp.replace(
    '[IP]',
    configuration.stationByIpDefault
  )
  return getLocalization(url)
}

export function getStationsByZip(zip) {
  const url = configuration.apiUrls.stationsByZip.replace('[ZIP]', zip)
  return getLocalization(url)
}

export async function getSchedulesByStation(
  stationId,
  date,
  allLivestreams,
  addSchedulesToHome = false
) {
  try {
    const url = configuration.contentUrls.getSchedules
      .replace('[DEVICETYPE]', Platform.deviceType)
      .replace('[STATION_ID]', stationId)
      .replace('[DATE]', date)
      .replace('[ALL_LIVESTREAMS]', allLivestreams)
    const response = await contentServiceGet(url)
    const channels = response.collections[0].content[0].channels
    const areChannelsAvailable = channels.length > 0
    store.dispatch({
      type: 'SET_LIVESTREAM_AVAILABILITY',
      payload: areChannelsAvailable,
    })
    if (areChannelsAvailable) {
      store.dispatch({
        type: 'SET_SCHEDULES',
        payload: {
          channels: channels,
          lastDayFetched: date,
          stationId,
        },
      })
      if (addSchedulesToHome) {
        store.dispatch({
          type: 'ADD_SCHEDULES_TO_HOME',
          payload: {
            channels: channels,
          },
        })
      }
    }
  } catch (error) {
    console.error(error)
  }
}

export async function getNextDaySchedulesByStation() {
  try {
    const {
      multilivestream: { stationId, lastDayFetched },
    } = store.getState()
    if (stationId === null) return
    const nextDay = dayjs(lastDayFetched).add(1, 'days').format('YYYY-MM-DD')
    const url = configuration.contentUrls.getSchedules
      .replace('[DEVICETYPE]', Platform.deviceType)
      .replace('[STATION_ID]', stationId)
      .replace('[DATE]', nextDay)
      .replace('[ALL_LIVESTREAMS]', true)
    const response = await contentServiceGet(url)
    const channels = response.collections[0].content[0].channels
    store.dispatch({
      type: 'ADD_SCHEDULES',
      payload: {
        channels: channels,
      },
    })
  } catch (error) {
    console.error(error)
  }
}

export function getFavoriteStation(uid) {
  const url = configuration.apiUrls.favoriteStations
    .replace('[UID]', uid)
    .replace('[DEVICETYPE]', Platform.deviceType)
  return contentServiceGet(url)
    .then((response) => {
      const content =
        response.collections &&
        response.collections[0] &&
        response.collections[0].content &&
        response.collections[0].content[0]
          ? response.collections[0].content[0]
          : null
      if (content) {
        return Promise.resolve({
          call_sign: content.call_sign,
          passportEligible: content.mvod_enabled,
          station_id: content.cid,
          station_title: content.title,
          black_logo: content.images['black-logo'],
          color_logo: content.images['color-logo'],
          station_scenery:
            content.images['station-scenery-11'] +
            '?resize=' +
            (PLATFORM_IS_SCALED ? '1280x720' : '1920x1080') +
            '&format=webp',
          white_logo: content.images['white-logo'],
          white_single_logo: content.images['white_single_logo']
            ? content.images['white_single_logo'] + '?format=webp'
            : '',
          white_cobranded_logo:
            content.images['white-cobranded-logo'] + '?format=webp',
          short_common_name: content.short_common_name,
          passport_learn_more_url: content.passport_learn_more_url,
          showMenu: null,
        })
      } else {
        return new Promise((resolve) => {
          getStationsByAuto().then((autoLocaliationResponse) => {
            getStationsByZip(autoLocaliationResponse.zipcode).then(
              (stationsByIpResponse) => {
                const station = stationsByIpResponse.stations[0]
                resolve({
                  call_sign: station.callsign,
                  passportEligible: false,
                  station_id: station.pbs_id,
                  station_title: station.common_name,
                  black_logo: '',
                  color_logo: '',
                  station_scenery: null,
                  white_logo: '',
                  white_cobranded_logo: '',
                  short_common_name: station.common_name_sort,
                  passport_learn_more_url: station.membership_url,
                  showMenu: null,
                })
              }
            )
          })
        })
      }
    })
    .catch((error) => {
      console.error(error)
      return Promise.reject(error)
    })
}

export function getShowPoster() {
  return contentServiceGet(
    'https://content.services.pbs.org/v3/samsungtv/collections/show-poster-highlight-fvod/'
  ).then((response) => response?.collections[0]?.content[0]?.image)
}

export function getActivationStatus() {
  const url = configuration.apiUrls.deviceActivationStatus.replace(
    '[deviceId]',
    platformStoreGet(deviceKey)
  )

  return profileServiceGet(url)
}
export function checkDeviceId() {
  let payload = {}
  //unique id for localStorage that is brand specific (ga/kids)
  const deviceId = platformStoreGet(deviceKey)
  //if device id is null, generate one
  if (deviceId) {
    //store existing localStorage version of deviceID in redux
    payload.deviceId = platformStoreGet(deviceKey)
  } else {
    //create a device id
    let deviceId

    if (COMPILING_FOR_VIZIO) {
      deviceId = `vizio-ga-${v4()}`
    } else if (COMPILING_FOR_COMCASTX1) {
      deviceId = `comcast-ga-${v4()}`
    } else {
      deviceId = `samsung-ga-${v4()}`
    }

    //store device id in local storage
    platformStoreSet(deviceKey, deviceId)

    //store new device id in redux
    payload.deviceId = deviceId
  }

  store.dispatch({
    type: 'UPDATE_DEVICE',
    payload,
  })
}

export function deleteActivationStatus() {
  platformStoreDelete(uidKey)
  platformStoreDelete(deviceKey)
  store.dispatch({
    type: 'UPDATE_DEVICE',
    payload: { UID: null },
  })
}

export function checkActivationStatus(callback) {
  let stationData
  // check if device id exists in localStorage
  getActivationStatus()
    .then((resp) => {
      //receive the response from the API and process it
      if (resp && resp.device && resp.device.UID) {
        // if localStorage version of the UID matches the returned UID then activate
        if (platformStoreGet(uidKey) === resp.device.UID) {
          //we have a UID, store in Redux
          store.dispatch({
            type: 'UPDATE_DEVICE',
            payload: { UID: resp.device.UID },
          })
          // window.ga('set', 'userId', resp.device.UID)
          //use the UID to get the local station information
          getFavoriteStation(resp.device.UID)
            .then((data) => {
              stationData = data
              store.dispatch({
                type: 'UPDATE_STATION',
                payload: data,
              })
              //Reset/Nullify Home Content so Home page can refresh when it mounts
              store.dispatch({
                type: 'SET_HOME_CONTENT',
                payload: { collections: null },
              })
              registerUser(resp.device.UID)
                .then(() => {
                  return callback(null)
                })
                .catch((error) => {
                  return callback({ type: 'registerUser', data: error })
                })
              if (COMPILING_FOR_COMCASTX1 && COMCAST_IN_APP_DONATIONS_ENABLED) {
                getStationDonationAmountsHelper(stationData.call_sign)
              }
            })
            .catch((error) => {
              return callback({ type: 'promise', data: error })
            })
        } else {
          deActivateDevice()
            .then(() => {
              return callback(null)
            })
            .catch(() => {
              return callback({ type: 'deactivate', data: {} })
            })
        }
      }
    })
    .catch((error) => {
      try {
        const csError = JSON.parse(error.response)
        if (csError) {
          switch (csError.errorCode) {
            case 500102:
              return callback(null)
            default:
              triggerSentryError(
                `csError.errorCode - ${csError.errorCode}`,
                'contentActions.js',
                {
                  errorMessage:
                    csError.message ?? 'Undefined error message (default)',
                  errorLocation: 'Content Action - getActivationStatus',
                  errorType: ComcastErrorType.restriction,
                },
                true
              )
              return callback({ type: 'csjsonerror', data: csError })
          }
        }
      } catch (error) {
        triggerSentryError(
          `csError Invalid JSON`,
          'contentActions.js',
          {
            errorMessage: error.message ?? 'Undefined error message (default)',
          },
          true
        )
        return callback({ type: 'cserror', data: error })
      }
    })
}

export function deActivateDevice() {
  const deactivateUrl = configuration.apiUrls.deviceDeactivation
    .replace('[UID]', platformStoreGet(uidKey))
    .replace('[deviceId]', platformStoreGet(deviceKey))

  return profileServiceDelete(deactivateUrl)
}

export function setStation(guid) {
  const url = configuration.apiUrls.setStation
    .replace('[UID]', platformStoreGet('uID-ga'))
    .replace('[GUID]', guid)
    .replace('[DEVICETYPE]', Platform.deviceType)

  if (MULTILIVESTREAM_ENABLED) {
    getSchedulesByStation(guid, new Date().toISOString().split('T')[0], true)
  }
  return contentServicePut(url, {
    flagship_station_id: guid,
  })
}

export function getStationShowMenu(stationId) {
  return contentServiceGet(
    configuration.apiUrls.stationShowMenu
      .replace('[STATION_ID]', stationId)
      .replace('[DEVICETYPE]', Platform.deviceType)
  )
}

export function acceptVppa(uid) {
  const url = configuration.apiUrls.getUserInfo
    .replace('[uid]', uid)
    .replace('[DEVICETYPE]', Platform.deviceType)
  return contentServicePatch(url, { vppa_accepted: true })
}

export function declineVppa(uid) {
  const url = configuration.apiUrls.getUserInfo
    .replace('[uid]', uid)
    .replace('[DEVICETYPE]', Platform.deviceType)
  return contentServicePatch(url, { vppa_accepted: false })
}

export function getLocalization(url) {
  return baseRequest(url, 'GET', null, null, true)
    .then((resp) => {
      return Promise.resolve(resp)
    })
    .catch((err) => {
      console.error('Error getting localization data', err)
      return Promise.reject(err)
    })
}

export async function getFavorites(uid) {
  try {
    const response = await contentServiceGet(
      configuration.apiUrls.getUserFavorites
        .replace('[uid]', uid)
        .replace('[DEVICETYPE]', Platform.deviceType)
    )
    store.dispatch({
      type: 'SET_FAVORITES',
      payload: {
        content: response.collections[0]?.content || [],
        url: response.collections[0]?.url,
        nextUrl: response.meta.links.next,
      },
    })
    return
  } catch (error) {
    store.dispatch({
      type: 'SET_FAVORITES',
      payload: {
        content: 'error',
      },
    })
  }
}

export async function getAllFavorites(uid) {
  try {
    let response = await contentServiceGet(
      configuration.apiUrls.getUserFavorites
        .replace('[uid]', uid)
        .replace('[DEVICETYPE]', Platform.deviceType)
    )
    let acc = response.collections[0].content
    while (response.meta.links.next) {
      response = await contentServiceGet(response.meta.links.next)
      acc = acc.concat(response.collections[0].content)
    }
    store.dispatch({
      type: 'SET_FAVORITES',
      payload: {
        content: acc,
        url: response.collections[0].url,
        nextUrl: response.meta.links.next,
      },
    })
    return acc
  } catch (error) {
    store.dispatch({
      type: 'SET_FAVORITES',
      payload: {
        content: 'error',
      },
    })
  }
}

export function addToFavorites(uid, pageDetails) {
  const changeFavorites = configuration.contentUrls.changeFavorites
    .replace('[uid]', uid)
    .replace('[cid]', pageDetails.cid)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServicePut(changeFavorites, {})
    .then(() => {
      store.dispatch({
        type: 'ADD_FAVORITE',
        payload: { favorite: pageDetails },
      })
      store.dispatch({
        type: 'ADD_HOME_FAVORITE',
        payload: { favorite: pageDetails },
      })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export function removeFromFavorites(uid, cid) {
  const changeFavorites = configuration.contentUrls.changeFavorites
    .replace('[uid]', uid)
    .replace('[cid]', cid)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceDelete(changeFavorites)
    .then(() => {
      store.dispatch({
        type: 'DELETE_FAVORITE',
        payload: { cid },
      })
      store.dispatch({
        type: 'DELETE_HOME_FAVORITE',
        payload: { cid },
      })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export async function getWatchlist(uid) {
  try {
    const response = await contentServiceGet(
      configuration.apiUrls.getUserWatchlist
        .replace('[uid]', uid)
        .replace('[DEVICETYPE]', Platform.deviceType)
    )
    store.dispatch({
      type: 'SET_WATCHLIST',
      payload: {
        content: response.collections[0]?.content || [],
        url: response.collections[0]?.url,
        nextUrl: response.meta.links.next,
      },
    })
    return
  } catch (error) {
    store.dispatch({
      type: 'SET_WATCHLIST',
      payload: {
        content: 'error',
      },
    })
  }
}
export async function getAllWatchlist(uid) {
  try {
    let response = await contentServiceGet(
      configuration.apiUrls.getUserWatchlist
        .replace('[uid]', uid)
        .replace('[DEVICETYPE]', Platform.deviceType)
    )
    let acc = response.collections[0].content
    while (response.meta.links.next) {
      response = await contentServiceGet(response.meta.links.next)
      acc = acc.concat(response.collections[0].content)
    }
    store.dispatch({
      type: 'SET_WATCHLIST',
      payload: {
        content: acc,
        url: response.collections[0].url,
        nextUrl: response.meta.links.next,
      },
    })
    return acc
  } catch (error) {
    store.dispatch({
      type: 'SET_WATCHLIST',
      payload: {
        content: 'error',
      },
    })
  }
}

export function addToWatchList(watchlistItem) {
  const changeWatchlist = configuration.contentUrls.changeWatchlist
    .replace('[uid]', store.getState().user.pid)
    .replace('[cid]', watchlistItem.cid)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServicePut(changeWatchlist, {})
    .then(() => {
      store.dispatch({
        type: 'ADD_WATCHLIST',
        payload: { watchlist: watchlistItem },
      })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export function removeFromWatchList(videoId) {
  const changeWatchlist = configuration.contentUrls.changeWatchlist
    .replace('[uid]', store.getState().user.pid)
    .replace('[cid]', videoId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceDelete(changeWatchlist, {})
    .then(() => {
      store.dispatch({
        type: 'DELETE_WATCHLIST',
        payload: { cid: videoId },
      })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export async function getWatchHistory(uid) {
  try {
    const url = configuration.apiUrls.getUserHistory
      .replace('[uid]', uid)
      .replace('[DEVICETYPE]', Platform.deviceType)
    let response = await contentServiceGet(url)
    store.dispatch({
      type: 'SET_WATCH_HISTORY',
      payload: {
        content: response.collections[0]?.content || [],
        url: response.collections[0]?.url,
        nextUrl: response.meta.links.next,
      },
    })
    return
  } catch (error) {
    store.dispatch({
      type: 'SET_WATCH_HISTORY',
      payload: {
        content: 'error',
      },
    })
  }
}

export async function getAllWatchHistory(uid) {
  try {
    let response = await contentServiceGet(
      configuration.apiUrls.getUserHistory
        .replace('[uid]', uid)
        .replace('[DEVICETYPE]', Platform.deviceType)
    )
    let acc = response.collections[0].content
    while (response.meta.links.next) {
      response = await contentServiceGet(response.meta.links.next)
      acc = acc.concat(response.collections[0].content)
    }
    store.dispatch({
      type: 'SET_WATCH_HISTORY',
      payload: {
        content: acc,
        url: response.collections[0].url,
        nextUrl: response.meta.links.next,
      },
    })
    return acc
  } catch (error) {
    store.dispatch({
      type: 'SET_WATCH_HISTORY',
      payload: {
        content: 'error',
      },
    })
  }
}

export function updateWatchHistory(videoId, timecode) {
  const url = configuration.apiUrls.getWatchProgress
    .replace('[uid]', store.getState().user.pid)
    .replace('[videoId]', videoId)
    .replace('[DEVICETYPE]', Platform.deviceType)
  const data = { timecode }

  return contentServicePut(url, data)
}

export function getCollections(
  url,
  identifier,
  exclude = [], //TODO: remove. nothing in GA currently utilized this argument in a meaningful way
  bypass404 = false
) {
  let localUrl = url
  const pid = store.getState().user.pid
  if (-1 === localUrl.indexOf('station_id') && -1 === localUrl.indexOf(pid)) {
    localUrl = appendUserId(localUrl, pid)
  }

  return contentServiceGet(localUrl)
    .then((resp) => {
      let collections = []
      if (resp.resource) {
        exclude.push(resp.resource.cid)
      }
      resp.collections.forEach((collection) => {
        if (collection.content) {
          let filteredContent = collection.content.filter(function (e) {
            return this.indexOf(e.cid) < 0
          }, exclude)
          collection.content = filteredContent
          collections.push(collection)
        }
      })

      collections = resp.collections.filter((collection) => {
        return collection.content && 0 !== collection.content.length
      })

      let collectionsResponse = {
        meta: resp.meta,
        ...(resp.resource && { resource: resp.resource }),
        collections,
      }
      return Promise.resolve(collectionsResponse)
    })
    .catch((err) => {
      if (!bypass404) {
        triggerSentryError(
          '[index] Error getting collections',
          'contentActions.js',
          {
            localUrl,
            errorMessage: err.message ?? 'Undefined error message (default)',
            errorLocation: 'Content Action - getCollections',
            errorType: ComcastErrorType.network,
          }
        )
      }

      return Promise.reject(err)
    })
}

export function getUpNext(videoId) {
  const {
    apiUrls: { getUpNext },
  } = configuration
  const url = getUpNext
    .replace('[uid]', store.getState().user.pid)
    .replace('[videoId]', videoId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return new Promise((resolve, reject) => {
    getCollections(url, 'upNext')
      .then((response) => {
        const upNextData = response.resource
        resolve({
          upNextShowTitle:
            upNextData?.parent?.season?.show?.title ||
            upNextData?.parent?.show?.title,
          upNextEpisodeTitle: upNextData.parent.title || upNextData.title,
          upNextEpisodeNumber: upNextData.parent.ordinal,
          upNextSeasonNumber:
            upNextData?.parent?.season?.ordinal || upNextData?.parent?.ordinal,
          upNextImage: upNextData.images['asset-mezzanine-16x9'],
          upNextVideoType: upNextData.video_type,
          upNextUrl: encodeURIComponent(upNextData.url),
          isMvod: upNextData.flags?.is_mvod,
          isPlayable: upNextData.flags?.is_playable,
        })
      })
      .catch((err) => {
        reject(err)
      })
  })
}

export async function getHomeData(stationId, userId) {
  try {
    const response = await contentServiceGet(
      configuration.contentUrls.getHomeData
        .replace('[STATION_ID]', stationId)
        .replace('[DEVICETYPE]', Platform.deviceType)
    )
    //filter out collections that do not have the content attribute but they aren't stub collections
    const collections = response.collections.filter((collection) => {
      return (
        (collection.content && 0 !== collection.content.length) ||
        collection['collection_type'] === 'stub'
      )
    })

    store.dispatch({
      type: 'SET_HOME_CONTENT',
      payload: {
        collections,
      },
    })

    if (MULTILIVESTREAM_ENABLED) {
      const date = new Date().toISOString().split('T')[0]
      getSchedulesByStation(stationId, date, true, true)
    }

    store.dispatch({
      type: 'UPDATE_SCREENSAVER_IMAGES',
      payload: {
        collections,
      },
    })

    response.collections
      .filter((c) => c['collection_type'] === 'stub')
      .forEach(async (collection) => {
        try {
          const response = await contentServiceGet(
            collection.url.replace('{user_id}', userId)
          )
          response.collections?.forEach((collection) => {
            store.dispatch({
              type: 'UPDATE_HOME_CONTENT_COLLECTION',
              payload: {
                collection,
                meta: response.meta,
              },
            })
          })
        } catch (error) {
          console.error(error)
        }
      })
  } catch (error) {
    store.dispatch({
      type: 'SET_HOME_CONTENT',
      payload: {
        collections: 'error',
      },
    })
  }
}

export function getVideoDetails(url) {
  const { pid, ignoreIsPlayable } = store.getState().user
  const now = new Date()

  let trimmedUrl = url.split('?')[0]
  let relatedUrl = `${trimmedUrl}related`
  trimmedUrl = `${trimmedUrl}?user_id=${pid}`
  relatedUrl = `${relatedUrl}/?user_id=${pid}`
  trimmedUrl = stripHttpProtocol(noCacheUrl(trimmedUrl))
  relatedUrl = stripHttpProtocol(relatedUrl)

  const identifier = `video-details-${noCacheUrl(trimmedUrl)}`
  const promises = []
  promises.push(getCollections(trimmedUrl, identifier))
  promises.push(
    getCollections(
      relatedUrl,
      `video-details-related-${trimmedUrl}`,
      [],
      true
    ).catch((err) => {
      return err
    })
  )

  return Promise.all(promises)
    .then(([collectionsResponse, relatedResponse]) => {
      const out = {}

      const videoResource = collectionsResponse.resource
      const { showTitle, episodeTitle } = getTitleAndSubtitle(videoResource)

      out.assetMezzanine =
        videoResource.images['asset-mezzanine-16x9'] ||
        videoResource.images['asset-kids-mezzanine1-16x9']

      let tracks = []
      videoResource.closed_captions.forEach((track) => {
        if ('WebVTT' === track.profile) {
          let trackInfo = {
            src: secureUrl(track.url),
            label: `English ${track.profile}`,
            kind: 'captions',
          }
          tracks.push(trackInfo)
        }
      })
      out.captionTracks = tracks

      out.collections = collectionsResponse.collections

      out.content_rating = videoResource.content_rating

      out.contentScope = videoResource.parent?.season?.show?.audience?.[0].scope

      //Three types of parent object structure.
      out.parentShowSlug =
        videoResource.parent?.season?.show?.slug ||
        videoResource.parent?.show?.slug ||
        videoResource.parent?.slug

      out.contentType = videoResource.video_type

      out.description_long = videoResource.description_long

      out.duration = videoResource.duration

      out.episodeTitle = episodeTitle

      if (videoResource.expire_date) {
        //Coerce
        out.expire_date = new Date(videoResource.expire_date)
      } else {
        const tomorrow = new Date()
        tomorrow.setDate(now.getDate() + 1)
        out.expire_date = tomorrow
      }
      out.expired = now.getTime() >= out.expire_date

      out.formattedOrdinals = getFormattedOrdinals(videoResource)

      out.funderMessage = videoResource.funder_message

      out.is_mvod = collectionsResponse.resource.flags?.is_mvod

      out.isPlayable = videoResource.flags?.is_playable || ignoreIsPlayable
      out.isPlayableExplanation = videoResource.flags?.is_playable_explanation

      out.legacyTpMediaId = videoResource.legacy_tp_media_id.toString()

      // TODO: only check preview? what about related_links.length || related_promos.length ?
      out.hasRelated = relatedResponse?.resource?.video_type === 'preview'
      out.relatedAssetUrl = relatedResponse?.resource?.url
      out.relatedAssetVideoType = relatedResponse?.resource?.video_type

      out.seconds_watched = videoResource.seconds_watched

      out.seriesTitle = videoResource.parent?.season?.show?.title
        ? videoResource.parent?.season?.show?.title
        : videoResource.parent?.title

      out.seriesTrackingEvent =
        videoResource.parent?.season?.show?.tracking_ga_event

      out.short_description = videoResource.short_description

      out.showTitle = showTitle

      out.url = secureUrl(
        videoResource.dash_drm_videos?.[0]?.url ||
          videoResource.hls_videos?.[0]?.url ||
          ''
      )
      out.drmWidevideLicenseUrl =
        videoResource.dash_drm_videos?.[0]?.widevine_license

      out.videoId = videoResource.cid

      out.slug = videoResource.slug

      out.contentUrl = videoResource.url
      out.parent = videoResource.parent
      out.trickPlayUrl = videoResource.trick_play_files?.find(
        (trickFile) => trickFile.filetype === 'vtt'
      )?.url

      return out
    })
    .catch((err) => {
      triggerSentryError('getVideoDetails', 'contentActions.js', {
        trimmedUrl,
        relatedUrl,
        errorMessage: err.message ?? 'Undefined error message (default)',
        errorLocation: 'Content Action - getVideoDetails',
        errorType: ComcastErrorType.network,
      })

      return Promise.reject(err)
    })
}

export function getPrivacyPolicy() {
  const url = configuration.apiUrls.privacyPolicyText.replace(
    '[DEVICETYPE]',
    Platform.deviceType
  )
  return contentServiceGet(url)
    .then((data) => Promise.resolve(data))
    .catch((error) => Promise.reject({ error, url }))
}

export function getClosedCaptions() {
  const url = configuration.apiUrls.closedCaptionText.replace(
    '[DEVICETYPE]',
    Platform.deviceType
  )

  return contentServiceGet(url)
    .then((data) => Promise.resolve(data))
    .catch((error) => Promise.reject({ error, url }))
}

export function getTermsOfUse() {
  const url = configuration.apiUrls.termsOfUseText.replace(
    '[DEVICETYPE]',
    Platform.deviceType
  )

  return contentServiceGet(url)
    .then((data) => Promise.resolve(data))
    .catch((error) => Promise.reject({ error, url }))
}

export function getSearch(stationId, query) {
  const url = configuration.apiUrls.search
    .replace('[QUERY]', query)
    .replace('[STATION_ID]', stationId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url).catch((error) => {
    triggerSentryError(
      `Error ${
        error.status ? error.status : '(unknown)'
      } on Search screen requesting content from ${url}`,
      'contentActions.js',
      {
        errorMesssage: error.message ?? 'Undefined error message (default)',
        errorLocation: 'Content Action - getSearch',
        errorType: ComcastErrorType.network,
      }
    )
  })
}

export function getMoreExtras(slug, stationId) {
  const url = configuration.contentUrls.getMoreExtras
    .replace('[slug]', slug)
    .replace('[STATION_ID]', stationId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url)
    .then((resp) => Promise.resolve({ resp, url }))
    .catch((error) => Promise.reject({ error, url }))
}

export function getAvailableSeasons(slug, stationId) {
  const url = configuration.contentUrls.getAvailableSeasons
    .replace('[slug]', slug)
    .replace('[STATION_ID]', stationId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url)
}

export function getExtrasInSeasons(
  slug,
  seasonId,
  stationId,
  activeSeason,
  url = null
) {
  if (!url) {
    url = configuration.contentUrls.getExtrasInSeasons
      .replace('[slug]', slug)
      .replace('[STATION_ID]', stationId)
      .replace('[SEASON_ID]', seasonId)
      .replace('[DEVICETYPE]', Platform.deviceType)
  }

  return getCollections(url, `season-${url}-${activeSeason}`).catch((error) =>
    Promise.reject({ error, url })
  )
}

export async function getPaginatedEpisodes(
  slug,
  seasonId,
  stationId,
  activeSeason
) {
  const url = configuration.contentUrls.getPaginatedEpisodes
    .replace('[slug]', slug)
    .replace('[SEASON_ID]', seasonId)
    .replace('[STATION_ID]', stationId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  let fetchMore = true
  let next = url
  const allEpisodes = []

  try {
    while (fetchMore) {
      const response = await getCollections(
        next,
        `season-${url}-${activeSeason}`
      )

      if (response.collections.length === 0) {
        fetchMore = false
      } else {
        next = response.meta.links.next
        allEpisodes.push(...response.collections[0].content)

        if (!next) {
          fetchMore = false
        }
      }
    }
  } catch (error) {
    return Promise.reject({ error, url })
  }

  return Promise.resolve(allEpisodes)
}

export function getSeasonDetails(slug, seasonId, stationId) {
  const url = configuration.contentUrls.getSeasonDetails
    .replace('[slug]', slug)
    .replace('[STATION_ID]', stationId)
    .replace('[SEASON_ID]', seasonId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url).catch((error) => Promise.reject({ error, url }))
}

export function getShowInfo(slug, stationId) {
  const url = configuration.contentUrls.getShowInfo
    .replace('[slug]', slug)
    .replace('[STATION_ID]', stationId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url).catch((error) => Promise.reject({ error, url }))
}

export function getShows(stationId) {
  const url = configuration.contentUrls.getShows
    .replace('[STATION_ID]', stationId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url)
}

export function getShowInfoByUser(slug, userId) {
  const url = configuration.contentUrls.getShowInfoByUser
    .replace('[slug]', slug)
    .replace('[USER_ID]', userId)
    .replace('[DEVICETYPE]', Platform.deviceType)

  return contentServiceGet(url)
}

const CONTINUE_WATCHING_SLUG = 'show-continue-watching'
const SEASON_ITEM_TYPE = 'season'
const SKYLAB_COLLECTION_ITEM_TYPE = 'skylab-collection'
const SHOW_CAROUSEL_SLUG = 'show-carousel'
const ASSET_MEZZANINE = 'asset-mezzanine-16x9'
const BACKGROUND_IMAGE = 'background'
const SHOW_MEZZANINE = 'show-mezzanine16x9'
const SHOW_LOGO = 'white-logo-41'

function initializePageData() {
  return {
    collections: [],
    pageDetails: {},
    continueWatchingData: {
      contentDescription: '',
      contentHasSecondsWatched: false,
      contentSecondsWatched: null,
      contentDuration: null,
      contentSeason: null,
      contentEpisode: null,
      contentTitle: '',
      contentMezzanineImage: '',
      contentUrl: '',
      contentResourceType: '',
    },
  }
}

export async function getShowDetails(slug, userId) {
  const pageData = initializePageData()

  try {
    const response = await getShowInfoByUser(slug, userId)
    if (response.collections?.length) {
      response.collections.forEach((collectionElement) => {
        if (collectionElement.slug === CONTINUE_WATCHING_SLUG) {
          setContinueWatchingCollection(pageData, collectionElement)
        } else if (collectionElement.item_type === SEASON_ITEM_TYPE) {
          const filteredCollection = filterContentPerCondition(
            collectionElement,
            hasEpisodesOrAssets
          )

          concatCollection(pageData, filteredCollection)
        } else if (
          collectionElement.item_type === SKYLAB_COLLECTION_ITEM_TYPE
        ) {
          const filteredCollection = filterContentPerCondition(
            collectionElement,
            hasVideos
          )

          concatCollection(pageData, filteredCollection)
        } else if (collectionElement.slug !== SHOW_CAROUSEL_SLUG) {
          concatCollection(pageData, collectionElement)
        }
      })

      setPageDetails(pageData, response)

      store.dispatch({
        type: 'SET_SHOW_DETAILS_DATA',
        payload: pageData,
      })
    }
  } catch (error) {
    console.error('err', error)
  }
}

function hasEpisodesOrAssets(seasonCollection) {
  return (
    seasonCollection.flags.has_episodes || seasonCollection.flags.has_assets
  )
}

function hasVideos(skylabCollection) {
  return skylabCollection.flags.has_videos
}

function setContinueWatchingCollection(pageData, collection) {
  if (collection.content.length !== 0) {
    const collectionContent = collection.content[0]

    pageData.continueWatchingData = {
      contentDescription: collectionContent?.description_short,
      contentHasSecondsWatched: collectionContent?.seconds_watched > 0,
      contentSecondsWatched: collectionContent?.seconds_watched,
      contentDuration: collectionContent?.duration,
      contentSeason: collectionContent?.parent?.season?.ordinal,
      contentEpisode: collectionContent?.parent?.ordinal,
      contentTitle: collectionContent?.title,
      contentMezzanineImage: collectionContent.images[ASSET_MEZZANINE] ?? null,
      contentUrl: collectionContent?.url,
      contentResourceType: collectionContent?.parent?.resource_type,
    }
  }
}

function setPageDetails(pageData, resp) {
  const { continueWatchingData } = pageData
  const pageDetails = resp.resource

  pageData.pageDetails = {
    ...pageDetails,
    mezzanineImage:
      (continueWatchingData?.contentMezzanineImage ||
        pageDetails.images[BACKGROUND_IMAGE] ||
        pageDetails.images[SHOW_MEZZANINE]) ??
      null,
    logo: pageDetails.images[SHOW_LOGO] ?? null,
  }
}

function filterContentPerCondition(collection, condition) {
  const collectionAux = { ...collection }
  collectionAux.content = collection.content.filter(condition)

  return collectionAux
}

function concatCollection(pageData, collection) {
  if (collection.content.length !== 0) {
    pageData.collections = pageData.collections.concat(collection)
  }
}

export async function getMessage() {
  const url = configuration.apiUrls.getMessageInformation.replace(
    '[DEVICETYPE]',
    Platform.deviceType
  )

  return contentServiceGet(url)
}
